/**
 * MerchantLectura — the Lectura preliminar / locked (roadmap v1.4.2 R1, U3).
 *
 * Renders the store baseline set (P3) — the first read GA4 cannot give: who arrives, what
 * they become, and where they leave. Preliminary = session rates locked; locked = the
 * conversion rates too. Nothing here is a verdict: shares, transitions and intervals,
 * in the merchant's words. Impersonal voice; every number carries its interval.
 *
 * Sources: `StoreInfo.baseline_set` (values as {value, c, n}) and `lifecycle` for the
 * phase copy. No live analytics call — the Lectura is the artifact, shown as locked.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, Text, Tag } from '@nimbus-ds/components'
import { useSetMerchantChrome } from '@/components/layout/MerchantChrome'
import { authService } from '@/api/auth'
import { storeService, type StoreInfo, type BaselineRate, type StoreBaselineSet } from '@/api/store'
import { goalService, type Lectura, type LecturaFact } from '@/api/goals'
import { trackAppEvent } from '@/lib/appTelemetry'
import { fmtInt, fmtMoney } from '@/lib/formatEsAr'

// ---- copy ------------------------------------------------------------------------------

const ENTRY_LABEL: Record<string, string> = {
  promo: 'llegan desde una campaña o un anuncio',
  untagged: 'entran directo o desde un link sin etiqueta',
  specific: 'entran directo a un producto, una categoría o una búsqueda',
  restock: 'vuelven a comprar algo que ya compraron',
  unlinked: 'compran sin que se registre la visita',
}
const ENTRY_SHORT: Record<string, string> = {
  promo: 'Campaña / anuncio', untagged: 'Directo / sin etiqueta', specific: 'Buscando algo puntual', restock: 'Reposición', unlinked: 'Sin visita registrada',
}
const STATE_LABEL: Record<string, string> = {
  incoming: 'se van sin mirar', browser: 'miran', shopper: 'buscan algo concreto', committed: 'llegan al checkout', converter: 'compran',
}
const DEVICE_LABEL: Record<string, string> = { mobile: 'celular', desktop: 'computadora', unknown: 'sin datos' }

// ---- numbers ---------------------------------------------------------------------------

/** Wilson 90% interval on a rate with counts; returns [lo, hi] as fractions. */
function wilson90(c: number, n: number): [number, number] {
  if (!n) return [0, 0]
  const z = 1.6449, p = c / n
  const d = 1 + (z * z) / n
  const centre = (p + (z * z) / (2 * n)) / d
  const half = (z * Math.sqrt((p * (1 - p)) / n + (z * z) / (4 * n * n))) / d
  return [Math.max(0, centre - half), Math.min(1, centre + half)]
}
const per100 = (r?: BaselineRate | null) => (r && r.value != null ? Math.round(r.value * 100) : null)
function per100Range(r?: BaselineRate | null): string | null {
  if (!r || r.value == null || !r.n) return null
  const [lo, hi] = wilson90(r.c, r.n)
  const a = Math.round(lo * 100), b = Math.round(hi * 100)
  return a === b ? `${a}` : `${a}–${b}`
}
const pct1 = (r?: BaselineRate | null) => (r && r.value != null ? `${(r.value * 100).toFixed(1)}%` : '—')

function fmtDate(iso?: string | null) {
  if (!iso) return ''
  try { return new Date(iso).toLocaleDateString('es-AR', { day: 'numeric', month: 'long' }) } catch { return iso.slice(0, 10) }
}

// ---- sections --------------------------------------------------------------------------

const TRANSITION_LABEL: Record<NonNullable<LecturaFact['transition']>, string> = {
  engagement: 'Quiénes se quedan', formation: 'Qué encuentran', close: 'Quiénes compran', repeat: 'Quiénes vuelven',
}

/**
 * The persisted facts (R2 P7) in FUNNEL ORDER — quiénes se quedan, qué encuentran, quiénes
 * compran — with the store-level fact first under each heading and segment facts under it.
 * The stake per 30 days is a label on the fact, never its position. Notices and references
 * come after, unnumbered. The range under a fact is the margin on its main rate.
 */
function FactsSection({ lectura }: { lectura: Lectura }) {
  const ordered = lectura.facts.filter((f) => f.order)
  const rest = lectura.facts.filter((f) => !f.order)
  if (lectura.facts.length === 0) return null
  const range = (f: LecturaFact) => {
    const r = f.numbers.rate
    if (!r || r.lo == null || r.hi == null) return null
    const a = Math.round(r.lo * 100), b = Math.round(r.hi * 100)
    return a === b ? null : `entre ${a} y ${b} de cada 100`
  }
  const groups = (['engagement', 'formation', 'close', 'repeat'] as const)
    .map((t) => ({ t, facts: ordered.filter((f) => f.transition === t) }))
    .filter((g) => g.facts.length > 0)
  return (
    <Card>
      <div className="flex flex-col gap-4">
        <Text fontWeight="medium">Lo que dicen tus números</Text>
        {groups.map((g) => (
          <div key={g.t} className="flex flex-col gap-2">
            <Text fontSize="caption" fontWeight="medium" color="neutral-textLow">{TRANSITION_LABEL[g.t]}</Text>
            <ol className="m-0 flex list-none flex-col gap-3 p-0">
              {g.facts.map((f) => (
                <li key={f.id} className="flex flex-col gap-1">
                  <Text>{f.sentence}</Text>
                  <div className="flex flex-wrap items-center gap-x-3">
                    {range(f) && <Text fontSize="caption" color="neutral-textLow">Margen: {range(f)}.</Text>}
                    {f.revenue_at_stake && f.revenue_at_stake.value > 0 && (
                      <Text fontSize="caption" color="neutral-textLow">{fmtMoney(f.revenue_at_stake.per_30d)} en juego cada 30 días</Text>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        ))}
        {rest.length > 0 && (
          <div className="flex flex-col gap-2 border-t border-[var(--nimbus-colors-neutral-surfaceHighlight)] pt-3">
            {rest.map((f) => (
              <Text key={f.id} fontSize="caption" color="neutral-textLow">{f.sentence}</Text>
            ))}
          </div>
        )}
        <Text fontSize="caption" color="neutral-textLow">
          Lo que está en juego es la facturación de la parte de la tienda de la que habla cada dato, llevada a 30 días. No es una mejora estimada.
        </Text>
      </div>
    </Card>
  )
}

function ProfileSentence({ set }: { set: NonNullable<StoreBaselineSet['session_rates']> }) {
  const shares = set.values.entry_class_shares || {}
  const parts = Object.entries(shares)
    .filter(([k, r]) => k !== 'unlinked' && r.value != null && r.value >= 0.01)
    .sort((a, b) => (b[1].value || 0) - (a[1].value || 0))
    .map(([k, r]) => `${per100(r)} ${ENTRY_LABEL[k] || k}`)
  if (parts.length === 0) return null
  return (
    <Card>
      <div className="flex flex-col gap-2">
        <Text fontWeight="medium">Quiénes llegan</Text>
        <Text>De cada 100 visitas, {parts.slice(0, -1).join(', ')}{parts.length > 1 ? ' y ' : ''}{parts[parts.length - 1]}.</Text>
        <Text fontSize="caption" color="neutral-textLow">Leído de la primera página de cada visita, antes de que la tienda muestre nada.</Text>
      </div>
    </Card>
  )
}

function StateTable({ set }: { set: NonNullable<StoreBaselineSet['session_rates']> }) {
  const s = set.values.state_shares
  const order = ['incoming', 'browser', 'shopper', 'committed', 'converter'] as const
  const rows = order.map((k) => ({ k, n: per100(s[k]), range: per100Range(s[k]) })).filter((r) => r.n != null)
  if (rows.length === 0) return null
  const t = set.values.transitions
  return (
    <Card>
      <div className="flex flex-col gap-3">
        <Text fontWeight="medium">Qué pasa en cada visita</Text>
        <Text>De cada 100 visitas: {rows.map((r) => `${r.n} ${STATE_LABEL[r.k]}`).join(' · ')}.</Text>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <tbody>
              {rows.map((r) => (
                <tr key={r.k} className="border-t border-[var(--nimbus-colors-neutral-surfaceHighlight)]">
                  <td className="py-1.5 pr-3 text-[var(--nimbus-colors-neutral-textLow)]">{STATE_LABEL[r.k]}</td>
                  <td className="py-1.5 pr-3 text-right font-medium tabular-nums">{r.n} de 100</td>
                  <td className="py-1.5 text-right tabular-nums text-[var(--nimbus-colors-neutral-textLow)]">{r.range}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Text fontSize="caption" color="neutral-textLow">
          De los que entran, {pct1(t.engagement)} se quedan a mirar; de los que miran, {pct1(t.formation)} encuentran algo concreto.
          El rango de la derecha es el margen con el que se leen estos números.
        </Text>
      </div>
    </Card>
  )
}

function DeviceSplit({ set }: { set: NonNullable<StoreBaselineSet['session_rates']> }) {
  const d = set.values.device_shares || {}
  const rows = Object.entries(d).filter(([, r]) => r.value != null && r.value >= 0.005).sort((a, b) => (b[1].value || 0) - (a[1].value || 0))
  if (rows.length === 0) return null
  return (
    <Card>
      <div className="flex flex-col gap-2">
        <Text fontWeight="medium">Desde dónde</Text>
        <Text>{rows.map(([k, r]) => `${per100(r)} de cada 100 desde ${DEVICE_LABEL[k] || k}`).join(' · ')}.</Text>
      </div>
    </Card>
  )
}

function ConversionBlock({ conv }: { conv: NonNullable<StoreBaselineSet['conversion_rates']> }) {
  const v = conv.values
  const byEntry = Object.entries(v.conversion_rate_by_entry_class || {})
    .filter(([k, r]) => k !== 'unlinked' && r.value != null && r.n >= 100)
    .sort((a, b) => (b[1].value || 0) - (a[1].value || 0))
  const byDevice = Object.entries(v.conversion_rate_by_device || {}).filter(([k, r]) => k !== 'unknown' && r.value != null && r.n >= 100)
  return (
    <Card>
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <Text fontWeight="medium">Cuántos compran</Text>
          {conv.confidence === 'low' && <Tag appearance="warning">Pocos datos</Tag>}
        </div>
        <Text>
          De cada 100 visitas compran {per100Range(v.conversion_rate) || '—'} ({pct1(v.conversion_rate)}).
          {v.close.value != null ? ` De los que buscan algo concreto, compran ${pct1(v.close)}.` : ''}
        </Text>
        {byEntry.length > 1 && (
          <Text fontSize="caption" color="neutral-textLow">
            Según cómo llegan: {byEntry.map(([k, r]) => `${ENTRY_SHORT[k] || k} ${pct1(r)}`).join(' · ')}.
          </Text>
        )}
        {byDevice.length > 1 && (
          <Text fontSize="caption" color="neutral-textLow">
            Por dispositivo: {byDevice.map(([k, r]) => `${DEVICE_LABEL[k] || k} ${pct1(r)}`).join(' · ')}.
          </Text>
        )}
        {v.aov.value != null && v.aov.conversions > 0 && (
          <Text fontSize="caption" color="neutral-textLow">Ticket promedio: ${fmtInt(Math.round(v.aov.value))}.</Text>
        )}
        {conv.locked_reason === 't_max' && (
          <Text fontSize="caption" color="neutral-textLow">Esta parte se leyó con pocas ventas; se ajusta sola cuando haya más.</Text>
        )}
      </div>
    </Card>
  )
}

// ---- page ------------------------------------------------------------------------------

export function MerchantLectura() {
  const [info, setInfo] = useState<StoreInfo | null>(null)
  const [lectura, setLectura] = useState<Lectura | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const storeId = authService.getCurrentStoreId()
    if (!storeId) { setLoading(false); return }
    Promise.all([
      storeService.getStore(storeId).then((i) => { setInfo(i); trackAppEvent('lectura_viewed', { lectura: i?.lifecycle?.lectura || 'none', facts: !!i?.lectura_read }) }),
      goalService.getLectura().then(setLectura).catch(() => {}),
    ])
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const set = info?.baseline_set || null
  const sr = set?.session_rates || null
  const cr = set?.conversion_rates || null
  const locked = !!cr
  useSetMerchantChrome({
    title: locked ? 'Lectura de tu tienda' : 'Primera lectura',
    subtitle: sr ? `Con visitas hasta el ${fmtDate(sr.provenance.window_end)} · ${fmtInt(sr.provenance.sessions)} visitas` : undefined,
    backTo: '/merchant',
    showStoreOnDesktop: true,
  })

  if (loading) return <Text className="py-6 text-center">Cargando…</Text>

  if (!sr) {
    return (
      <div className="flex flex-col gap-5">
        <Card>
          <div className="flex flex-col gap-2 py-2">
            <Text fontSize="highlight" fontWeight="medium">Todavía no hay una lectura de tu tienda.</Text>
            <Text color="neutral-textLow">Aparece sola cuando hay al menos una semana de visitas registradas. Mientras tanto, la tarea de la semana está en el inicio.</Text>
            <Link to="/merchant" className="text-sm">Volver al inicio</Link>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-5 md:gap-6">
      <div className="flex items-center gap-2">
        <Tag appearance={locked ? 'success' : 'primary'}>{locked ? 'Lectura completa' : 'Lectura preliminar'}</Tag>
        <Text fontSize="caption" color="neutral-textLow">
          {locked
            ? `Leída con ${fmtInt(cr!.values.conversion_rate.c)} ventas y ${fmtInt(sr.provenance.sessions)} visitas.`
            : 'Las visitas ya alcanzan; las ventas todavía no. Esa parte aparece sola cuando haya suficientes.'}
        </Text>
      </div>
      {lectura && <FactsSection lectura={lectura} />}
      <ProfileSentence set={sr} />
      <StateTable set={sr} />
      {cr && <ConversionBlock conv={cr} />}
      <DeviceSplit set={sr} />
      <Text fontSize="caption" color="neutral-textLow">
        Esta lectura queda fija como punto de partida: todo lo que cambie en tu tienda se compara contra estos números.
      </Text>
    </div>
  )
}
